import { zodResolver } from '@hookform/resolvers/zod';
import { G } from '@mobily/ts-belt';
import { validateImageUrl } from '@scale/frontend-lib/dashboard-api/multimodal';
import {
  ValidateImageUrlRequestPayload,
  ValidateImageUrlRequestPayloadSchema,
} from '@scale/mm-shared';
import { Button, TextField, Typography } from '@scale/scaleui';
import {
  Attachment,
  AttachmentSource,
  ModelInputFileAttachmentType,
} from '@scale/shared/types/chat';
import { useState, useContext } from 'react';
import Countdown from 'react-countdown';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useChatStoreExtract } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';
import uuid from 'uuid';

const schemaResolver = zodResolver(ValidateImageUrlRequestPayloadSchema);
const DEFAULT_FORM_VALUES: ValidateImageUrlRequestPayload = { url: '' };

interface ChatImageUrlAttachmentToolProps {
  className?: string;
  onSubmit: (attachment: Attachment) => void;
  disabled?: boolean;

  /** transformUrl is called on the input url before being validated or calling onSubmit */
  transformUrl?: (url: string) => string;
}

const ChatImageUrlAttachmentTool = ({
  className,
  onSubmit,
  disabled,
  transformUrl,
}: ChatImageUrlAttachmentToolProps) => {
  const form = useForm<ValidateImageUrlRequestPayload>({
    resolver: schemaResolver,
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const [coolDownUntil, setCoolDownUntil] = useState<number | null>(null);
  const config = useChatStoreExtract(state => state.config);
  const { projectId } = useContext(ChatTaskContext);

  const {
    mutateAsync,
    data: validationErrors,
    isLoading,
    isSuccess,
  } = useMutation(async (url: string): Promise<string[]> => {
    const { data, rateLimited } = await validateImageUrl({
      url,
      config,
      projectId,
    });

    if (rateLimited) {
      setCoolDownUntil(Date.now() + 10000);
      toast.error('Too many queries, please wait 10 seconds', { position: 'bottom-right' });
      return [];
    }

    const { isValid, metadata, errors } = data;
    if (isValid) {
      // image might not be valid if validation is broken
      toast.success('Image is allowed', { position: 'bottom-right' });
      onSubmit({
        url: metadata.url,
        mimeType: metadata.contentType,
        fileType: ModelInputFileAttachmentType.Image,
        fileSizeInBytes: metadata.contentLength ?? 0,
        name:
          metadata.url
            ?.split('/')
            ?.pop()
            ?.split(/(.jpg|.png)/)[0] ?? '',
        imageSubmissionMethod: AttachmentSource.URL,
      });
      form.reset();
      return [];
    } else {
      toast.error('Image is invalid (see explanation)', { position: 'bottom-right' });
      return errors;
    }
  });

  const isDisabled =
    disabled ||
    isLoading ||
    coolDownUntil !== null ||
    form.formState.isSubmitting ||
    !form.watch('url').length ||
    Object.values(form.formState.errors).some(G.isNotNullable);

  const handleSubmit = form.handleSubmit(({ url }) => {
    if (isDisabled) return;
    const transformedUrl = transformUrl != null ? transformUrl(url) : url;
    toast.promise(
      mutateAsync(transformedUrl),
      {
        pending: 'Validating...',
        error: 'Error validating',
      },
      { position: 'bottom-right', closeButton: true },
    );
  });

  return (
    <div className={twMerge('flex flex-col gap-2 w-full max-h-full', className ?? '')}>
      <form className="flex justify-between w-full gap-2" onSubmit={handleSubmit}>
        <TextField
          {...form.register('url')}
          className="flex-grow max-w-full"
          leadingIcon="link"
          placeholder="Enter image URL"
          onKeyDown={async e => e.key === 'Enter' && !!form.watch('url').length && handleSubmit()}
          error={form.formState.errors.url != null}
          description={form.formState.errors.url ? form.formState.errors.url.message : ''}
          value={form.watch('url')}
        />
        <Button
          variant="primary"
          type="submit"
          className="h-fit p-2 rounded-md"
          size="lg"
          onClick={handleSubmit}
          disabled={isDisabled}
        >
          Submit
        </Button>
      </form>
      {coolDownUntil !== null && (
        <Countdown
          date={coolDownUntil}
          onComplete={() => setCoolDownUntil(null)}
          renderer={({ seconds }) => (
            <Typography.Caption className="text-danger-600">
              Too many searches, please wait {seconds} seconds
            </Typography.Caption>
          )}
        />
      )}
      {isSuccess && validationErrors.length > 0 && (
        <div className="flex flex-col p-2 gap-2 text-wrap overflow-hidden">
          <Typography.Caption>Errors from last submission</Typography.Caption>
          <ul className="list-disc list-inside">
            {validationErrors.map(errorMessage => (
              <li className="ml-2" key={uuid.v4()}>
                <Typography.Caption className="text-danger-600">{errorMessage}</Typography.Caption>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ChatImageUrlAttachmentTool;
